import { Frame, Label, fx } from './frame';
import { useStrings } from '../../../state/useLocale';
import type { FigureProps } from './index';

/**
 * The two figures about reading output you did not write.
 *
 * A test report and a stack trace are both long, both mostly irrelevant, and
 * both have exactly one line that tells you where to go. So both drawings do
 * the same thing: print the whole thing, then take the weight off everything
 * except that line.
 *
 * `test-run` is the one figure in the set that spends no Signal Green at all.
 * Green means "passed" everywhere else in this product — a filled objective
 * mark, the cleared level, HEAD — so colouring a failing test with it would
 * teach the wrong thing in the one place the learner is looking hardest. The
 * failure is marked the way the rest of the system marks state instead: with
 * lightness, weight and a ground.
 *
 * Nothing inside either well is translated. The test names are the names in
 * somebody's spec file, and `TypeError: total is not a function` with the
 * three frames under it is what the runtime printed — a drawing that
 * translated them would be a drawing of a screen the learner will never see
 * (docs/i18n.md §6.1). The summary row and the two pointers are the author's.
 */

/* ── test-run ────────────────────────────────────────────────────────────────
   Three tests reported one at a time, the way a runner reports them, then the
   one that failed lifted onto its own ground. The tick and the cross are drawn
   rather than typed so they stay sharp at this size and do not depend on the
   mono face carrying the glyphs. */

export function TestRunFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;
  const tests = [
    { name: 'adds items to the cart', y: 36, at: 180, pass: true },
    { name: 'applies the discount', y: 58, at: 420, pass: true },
    { name: 'includes tax in the total', y: 80, at: 660, pass: false },
  ];

  return (
    <Frame name="test-run" active={active}>
      <rect className="well fx-in" style={fx(0)} x={14} y={18} width={292} height={84} rx={2} />

      {/* The failing row's ground, under the row rather than over it. */}
      <g className="fx-in" style={fx(860)}>
        <rect className="row-hot" x={14} y={70} width={292} height={22} />
        <rect className="fig-rail" x={14} y={70} width={2} height={22} />
      </g>

      {tests.map((test) => (
        <g key={test.name} className="fx-rise" style={fx(test.at)}>
          {test.pass ? (
            <path className="mark" d={`M22 ${test.y - 0.5} L25 ${test.y + 3} L31 ${test.y - 4.5}`} />
          ) : (
            <path className="mark mark-fail" d={`M22 ${test.y - 4} L30 ${test.y + 4} M30 ${test.y - 4} L22 ${test.y + 4}`} />
          )}
          <text className={test.pass ? 'lbl-dim' : 'lbl-max'} x={40} y={test.y}>
            {test.name}
          </text>
        </g>
      ))}

      {/* Three columns, placed rather than spaced: SVG collapses a run of
          spaces to one, so a gap has to be an x. The counts are the drawing's
          own, so they are passed to the catalogue rather than written into
          it — Arabic agrees a noun with its number, and `3 اختبارات` is not
          `1 اختبار` with a letter on the end. */}
      <g className="fx-in" style={fx(1040)}>
        <Label className="lbl-dim" x={14} y={122} at="left">
          {f.testCount(tests.length)}
        </Label>
        <Label className="lbl-dim" x={72} y={122} at="left">
          {f.passedCount(tests.filter((t) => t.pass).length)}
        </Label>
        <Label className="lbl-max" x={136} y={122} at="left">
          {f.failedCount(tests.filter((t) => !t.pass).length)}
        </Label>
      </g>
    </Frame>
  );
}

/* ── stack-trace ─────────────────────────────────────────────────────────────
   The throw site is at the top and the frames run outward from it, which is
   why the top line is almost never the line to open. The drawing prints all
   three frames at full strength and then takes the weight off the two that are
   not yours, leaving the one file the learner can actually edit. That frame is
   the only green mark in the figure, and it is a rail rather than a fill: the
   line is not passing or failing, it is just the one to open. */

export function StackTraceFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;

  return (
    <Frame name="stack-trace" active={active}>
      <rect className="well fx-in" style={fx(0)} x={14} y={16} width={292} height={100} rx={2} />

      <text className="fx-rise" style={fx(120)} x={24} y={36}>
        <tspan className="lbl-max">TypeError:</tspan>
        <tspan className="lbl-ink"> total is not a function</tspan>
      </text>

      {/* The frames that are not yours, in one group so one beat covers both. */}
      <g className="fx-dim" style={fx(700, { '--t': '300ms' })} opacity={0.45}>
        <text className="lbl-dim fx-rise" style={fx(240)} x={36} y={58}>
          at sum (node_modules/big.js:91)
        </text>
        <text className="lbl-dim fx-rise" style={fx(440)} x={36} y={98}>
          at node:internal/run_main:129
        </text>
      </g>

      <text className="lbl-ink fx-rise" style={fx(340)} x={36} y={78}>
        at Cart.total (cart.js:42)
      </text>

      <rect className="sig fx-pop" style={fx(800)} x={24} y={69} width={2.5} height={13} rx={1} />
      <Label className="fx-in" style={fx(880)} x={200} y={78} at="left">
        {f.yourCode}
      </Label>

      <Label className="lbl-dim fx-in" style={fx(1020)} x={14} y={136} at="left">
        {f.firstFrame}
      </Label>
    </Frame>
  );
}
